import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { logger } from '../../utils/logger';
import { useAnalytics } from '../../hooks/useAnalytics';
import '../../styles/VerificationModal.css';

interface VerificationSuccessMessageProps {
  onClose?: () => void;
  redirectDelay?: number;
  redirectTo?: string;
}

const VerificationSuccessMessage: React.FC<VerificationSuccessMessageProps> = ({
  onClose,
  redirectDelay = 5,
  redirectTo = '/my-profile'
}) => {
  const navigate = useNavigate();
  const { trackCustomEvent } = useAnalytics();
  const [countdown, setCountdown] = React.useState<number>(redirectDelay);

  // Track the verification success event
  useEffect(() => {
    trackCustomEvent('email_verification_success_viewed', {
      redirect_destination: redirectTo
    });
    logger.info('✅ Displaying verification success message');
  }, [trackCustomEvent, redirectTo]);

  // Handle the countdown and automatic redirect
  useEffect(() => {
    if (countdown <= 0) {
      logger.info(`🔄 Auto-redirecting to ${redirectTo} after verification success`);
      navigate(redirectTo);
      return;
    }

    const timer = setInterval(() => {
      setCountdown(prev => Math.max(0, prev - 1));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [countdown, navigate, redirectTo]);

  // Handle manual redirect
  const handleContinue = () => {
    logger.info(`👆 User clicked continue after verification success, navigating to ${redirectTo}`);
    if (onClose) {
      onClose();
    }
    navigate(redirectTo);
  };
  return (
    <div className="verification-success-message">
      <FontAwesomeIcon icon={faCheckCircle} className="verification-success-icon" />
      <h3>Email Verified Successfully!</h3>
      <p>Thank you for verifying your email address. Your account is now fully activated and you can apply for events!</p>
      <p className="verification-success-info">You'll now see a green "Verified" button next to your email address in your profile.</p>
      
      <div className="verification-success-actions">
        <p className="countdown-text">
          <FontAwesomeIcon icon={faSpinner} spin className="icon-left" />
          Redirecting in {countdown} seconds...
        </p>
        <button 
          className="verification-action-button primary"
          onClick={handleContinue}
        >
          Continue Now
        </button>
      </div>
    </div>
  );
};

export default VerificationSuccessMessage;
